import { useState } from 'react';
import { Section } from '~/components/layout';
import type { PayrollSavingsCalculatorContent } from '~/content/types';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const MAX_EMPLOYEES = 10_000;
const MAX_FEE = 10_000;

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Compares nsave's $0 transfer fee with the visitor's own current fee. */
export function PayrollSavingsCalculator({
  content,
}: {
  content: PayrollSavingsCalculatorContent;
}) {
  const [employees, setEmployees] = useState(10);
  const [feeInput, setFeeInput] = useState('5');
  const parsedFee = Number.parseFloat(feeInput);
  const fee = Number.isFinite(parsedFee) && parsedFee >= 0 ? parsedFee : 0;
  const currentCost = employees * fee;
  const formattedSavings =
    currentCost >= 1_000_000 ? compactUsd.format(currentCost) : usd.format(currentCost);

  const updateEmployees = (amount: number) => {
    setEmployees((value) => clampNumber(value + amount, 1, MAX_EMPLOYEES));
  };

  const updateFee = (amount: number) => {
    setFeeInput(String(clampNumber(Math.round((fee + amount) * 100) / 100, 0, MAX_FEE)));
  };

  const updateFeeInput = (value: string) => {
    if (value === '') {
      setFeeInput(value);
      return;
    }

    const parsedValue = Number(value);
    setFeeInput(Number.isFinite(parsedValue) ? String(clampNumber(parsedValue, 0, MAX_FEE)) : '0');
  };

  return (
    <Section className="!pt-0">
      <div className="rounded-[28px] bg-grey-50 px-7 py-10 md:px-10 md:py-14 lg:px-14">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-h3 leading-tight font-semibold tracking-[-0.03em] text-balance md:text-h2">
            {content.title}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-body text-grey-700">{content.description}</p>
        </div>

        <div className="mx-auto mt-10 max-w-4xl rounded-[22px] bg-white p-6 md:p-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <CalculatorInput
              id="payroll-employees"
              label={content.employeesLabel}
              value={employees}
              min={1}
              max={MAX_EMPLOYEES}
              step={1}
              onChange={(value) => setEmployees(clampNumber(Number(value) || 1, 1, MAX_EMPLOYEES))}
              onDecrease={() => updateEmployees(-1)}
              onIncrease={() => updateEmployees(1)}
            />
            <CalculatorInput
              id="payroll-fee"
              label={content.feeLabel}
              value={feeInput}
              min={0}
              max={MAX_FEE}
              step="0.01"
              prefix="$"
              onChange={updateFeeInput}
              onDecrease={() => updateFee(-0.5)}
              onIncrease={() => updateFee(0.5)}
            />
          </div>
          <div
            aria-live="polite"
            className="mt-8 flex flex-col gap-4 border-t border-black-6 pt-7 sm:flex-row sm:items-end sm:justify-between">
            <span className="text-body-s text-grey-700">
              {content.savingsLabel} {content.perRunLabel}
            </span>
            <strong className="min-w-0 text-[clamp(3rem,6vw,5rem)] leading-none font-semibold tracking-[-0.05em] text-red-500 tabular-nums">
              {formattedSavings}
            </strong>
          </div>
        </div>
      </div>
    </Section>
  );
}

function CalculatorInput({
  id,
  label,
  prefix,
  value,
  min,
  max,
  step,
  onChange,
  onDecrease,
  onIncrease,
}: {
  id: string;
  label: string;
  prefix?: string;
  value: number | string;
  min: number;
  max: number;
  step: number | string;
  onChange: (value: string) => void;
  onDecrease: () => void;
  onIncrease: () => void;
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-body-s font-medium text-grey-700">
        {label}
      </label>
      <div className="mt-3 grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-3 rounded-full bg-grey-50 p-1.5 transition-shadow focus-within:ring-2 focus-within:ring-red-500/20">
        <button
          type="button"
          aria-label={`− ${label}`}
          onClick={onDecrease}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-grey-100 text-h4 text-ink transition-colors hover:bg-grey-200">
          −
        </button>
        <span
          className={
            prefix
              ? 'grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-1'
              : 'flex min-w-0 items-center justify-center'
          }>
          {prefix && (
            <span className="text-right text-h4 font-semibold text-grey-500">{prefix}</span>
          )}
          <input
            id={id}
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="min-w-0 w-full bg-transparent text-center text-h4 font-semibold tracking-[-0.02em] text-ink outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </span>
        <button
          type="button"
          aria-label={`+ ${label}`}
          onClick={onIncrease}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-grey-100 text-h4 text-ink transition-colors hover:bg-grey-200">
          +
        </button>
      </div>
    </div>
  );
}
